import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Truck, Phone, Mail, Clock, Shield, Zap, MapPin, ArrowRight,
  CheckCircle2, Star, ChevronDown, Package, Timer, FileCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1758523670564-d1d6a734dc0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxwYXJjZWwlMjBkZWxpdmVyeSUyMHNlcnZpY2V8ZW58MHx8fHwxNzc2NDQ4MTM2fDA&ixlib=rb-4.1.0&q=85";
const DELIVERY_IMG = "https://images.unsplash.com/photo-1605882174146-a464b70cf691?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxwYXJjZWwlMjBkZWxpdmVyeSUyMHNlcnZpY2V8ZW58MHx8fHwxNzc2NDQ4MTM2fDA&ixlib=rb-4.1.0&q=85";
const LONDON_IMG = "https://images.unsplash.com/photo-1764416166626-7f146bd8e36d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxMb25kb24lMjBjaXR5JTIwc3RyZWV0JTIwdmFuJTIwdHJhbnNwb3J0fGVufDB8fHx8MTc3NjQ0NjgxNXww&ixlib=rb-4.1.0&q=85";

const FAQ_DATA = [
  {
    q: "How fast can you deliver a parcel in London?",
    a: "We offer same day courier service across London. Depending on traffic and distance, most local deliveries are completed within 2 to 4 hours of collection. For urgent requests, call us directly on +44 7757 133163 and we will prioritise your delivery."
  },
  {
    q: "What size parcels can your courier service handle?",
    a: "We handle everything from small envelopes and boxes to large, bulky items that standard couriers refuse. If it fits in a van, we can deliver it. For oversized items, check out our dedicated <a href='/services/furniture-delivery-london'>furniture delivery</a> or <a href='/#services'>specialist transport</a> services."
  },
  {
    q: "Do you offer business courier accounts?",
    a: "Yes, we work with businesses across London who need regular courier and delivery services. Whether you send a few parcels a week or dozens a day, we can offer competitive rates and reliable, consistent service. Contact us to discuss your requirements."
  },
  {
    q: "Is my parcel insured during delivery?",
    a: "Yes. All items we transport are covered by our comprehensive goods-in-transit insurance. From the moment we collect your parcel to the point it is delivered, your goods are fully protected."
  },
  {
    q: "Can you collect from a business and deliver to a residential address?",
    a: "Absolutely. We handle business-to-business, business-to-consumer, and consumer-to-consumer deliveries. We collect from offices, warehouses, shops, and homes, and deliver to any UK address."
  },
  {
    q: "Do you deliver outside of London?",
    a: "Yes, while our base is in London, we provide nationwide courier services. We regularly deliver parcels from London to cities like Manchester, Birmingham, Leeds, Glasgow, and everywhere in between."
  }
];

const AREAS = [
  "City of London", "Canary Wharf", "Shoreditch", "Soho", "Mayfair",
  "Kings Cross", "Stratford", "Brixton", "Peckham", "Fulham",
  "Hammersmith", "Putney", "Wimbledon", "Clapham", "Battersea",
  "Dalston", "Islington", "Angel", "Finsbury Park", "Tottenham",
  "Wembley", "Acton", "Chiswick", "Deptford", "Woolwich"
];

function FAQItem({ question, answer }) {
  return (
    <details className="group border border-slate-200 rounded-lg" data-testid="faq-item">
      <summary className="flex items-center justify-between cursor-pointer p-5 text-left font-semibold text-slate-900 hover:text-orange-600 transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>
        {question}
        <ChevronDown className="w-5 h-5 text-slate-400 group-open:rotate-180 transition-transform flex-shrink-0 ml-4" />
      </summary>
      <div className="px-5 pb-5 text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: answer }} />
    </details>
  );
}

export default function CourierServiceLondonPage() {
  const navigate = useNavigate();

  const setMeta = useCallback((attr, attrVal, content) => {
    let el = document.querySelector(`meta[${attr}="${attrVal}"]`);
    if (!el) { el = document.createElement("meta"); el.setAttribute(attr, attrVal); document.head.appendChild(el); }
    el.setAttribute("content", content);
    el.setAttribute("data-page", "courier-service-london");
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const pageTitle = "Courier Service London | Same Day | Transport Anything 247";
    const originalTitle = document.title;
    document.title = pageTitle;
    const titleTimer = setTimeout(() => { document.title = pageTitle; }, 100);

    setMeta("name", "description", "Same day courier service London. Dedicated parcel, document and urgent deliveries. Fully insured, 24/7, instant transparent quotes. Call +44 7757 133163.");
    setMeta("name", "keywords", "courier service London, same day courier London, parcel delivery London, urgent courier London, document delivery London");
    setMeta("property", "og:title", "Courier Service London | Transport Anything 247");
    setMeta("property", "og:description", "Same day courier service in London. Dedicated parcel, document and urgent deliveries, fully insured, with instant transparent quotes.");
    setMeta("property", "og:type", "website");

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
    canonical.setAttribute("href", "https://transportanything247.co.uk/services/courier-service-london");
    canonical.setAttribute("data-page", "courier-service-london");

    return () => { document.title = originalTitle; clearTimeout(titleTimer); document.querySelectorAll('[data-page="courier-service-london"]').forEach(el => el.remove()); };
  }, [setMeta]);

  const structuredData = {
    "@context": "https://schema.org", "@type": "Service", "name": "Courier Service London",
    "provider": { "@type": "LocalBusiness", "name": "Transport Anything 247", "telephone": "+447757133163", "email": "customers@trasportanything247.com", "url": "https://transportanything247.co.uk", "areaServed": { "@type": "City", "name": "London" }, "openingHours": "Mo-Su 00:00-23:59" },
    "description": "Same day courier service in London. Dedicated parcel, document and urgent deliveries, fully insured, with instant transparent quotes. Available 24/7.", "areaServed": "London, United Kingdom", "serviceType": "Courier Service"
  };
  const faqStructuredData = {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": FAQ_DATA.map(item => ({ "@type": "Question", "name": item.q, "acceptedAnswer": { "@type": "Answer", "text": item.a.replace(/<[^>]*>/g, '') } }))
  };

  return (
    <div className="min-h-screen" data-testid="courier-service-london-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }} />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-2" data-testid="nav-logo">
              <Truck className="h-8 w-8 text-orange-500" />
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Transport Anything 247</span>
            </a>
            <div className="hidden md:flex items-center gap-6">
              <a href="/#calculator" className="nav-link">Get Quote</a>
              <a href="/#services" className="nav-link">Services</a>
              <a href="/#contact" className="nav-link">Contact</a>
              <a href="/blog" className="nav-link">Blog</a>
            </div>
            <a href="tel:+447757133163" className="hidden sm:flex items-center gap-2 text-orange-500 font-medium" data-testid="nav-phone">
              <Phone className="h-4 w-4" /><span>+44 7757 133163</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-16 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Couple receiving parcel delivery at doorstep in London" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="max-w-2xl">
            <p className="text-orange-400 font-medium text-sm tracking-wider uppercase mb-4">Fast & Reliable Deliveries</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Courier Service <span className="text-orange-500">London</span>
            </h1>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed max-w-xl">
              Need a same day courier in London? We collect and deliver parcels, documents, and packages across the capital with <strong className="text-white">dedicated, fully-insured transport</strong>. Instant transparent quotes online, available around the clock.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={() => navigate("/")} className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-8 py-6 text-base rounded-md shadow-lg hover:shadow-xl active:scale-95 transition-all" data-testid="hero-get-quote-btn">
                Get an Instant Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <a href="tel:+447757133163" className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white text-white font-medium px-8 py-3 rounded-md transition-colors" data-testid="hero-call-btn">
                <Phone className="h-5 w-5" /> Call Now
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-6 mt-10 text-sm text-slate-300">
              <span className="flex items-center gap-2"><Timer className="h-4 w-4 text-orange-400" /> Same Day Delivery</span>
              <span className="flex items-center gap-2"><Shield className="h-4 w-4 text-orange-400" /> Fully Insured</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-orange-400" /> 24/7 Service</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-slate-900 py-5 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: Zap, label: "2-4 Hour Local Delivery" },
              { icon: Shield, label: "Goods-in-Transit Insurance" },
              { icon: Star, label: "5-Star Customer Rating" },
              { icon: MapPin, label: "London & UK-Wide" },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-center gap-2 text-slate-300 text-sm">
                <item.icon className="h-4 w-4 text-orange-500 flex-shrink-0" /><span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Our Courier Services in London
            </h2>
            <p className="text-slate-600 text-base leading-relaxed">
              From a single envelope to pallets of stock, our London courier service is built for speed, reliability, and care.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: "Same Day Courier", desc: "Urgent deliveries collected and delivered the same day across London. We prioritise speed without compromising on care. Ideal for time-sensitive documents and packages." },
              { icon: Package, title: "Parcel & Package Delivery", desc: "Boxes, parcels, and packages of all sizes delivered reliably. Perfect for online sellers, small businesses, and anyone who needs items moved quickly across London." },
              { icon: FileCheck, title: "Document Courier", desc: "Confidential documents, contracts, legal papers, and important mail delivered securely and promptly. Trusted by solicitors, accountants, and businesses across the city." },
              { icon: Truck, title: "Business-to-Business", desc: "Regular B2B deliveries for London companies. Stock transfers, supplier collections, client deliveries, and inter-office moves handled with professional efficiency." },
              { icon: Timer, title: "Urgent & Emergency", desc: "When standard delivery is not fast enough. Our emergency courier service operates 24/7 for critical deliveries that simply cannot wait until tomorrow." },
              { icon: MapPin, title: "Nationwide Delivery", desc: "Need something sent from London to anywhere in the UK? We provide reliable long-distance courier services from the capital to every corner of the country." },
            ].map((service, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all group">
                <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-100 transition-colors">
                  <service.icon className="h-6 w-6 text-orange-500" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{service.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center text-slate-600 text-sm leading-relaxed max-w-2xl mx-auto">
            <p>
              Need something bigger moved? Try our <a href="/services/man-and-van-london" className="text-orange-500 hover:underline font-medium">man and van</a>, <a href="/services/car-transport-london" className="text-orange-500 hover:underline font-medium">car transport</a>, or <a href="/services/furniture-delivery-london" className="text-orange-500 hover:underline font-medium">furniture delivery</a> services.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Why Use Our Courier Service in London?
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                London has no shortage of courier companies, so what makes Transport Anything 247 different? Here is why our customers keep coming back:
              </p>
              <div className="space-y-4">
                {[
                  { title: "Genuinely Fast", desc: "We do not batch deliveries or take detours. Your parcel gets collected and goes straight to its destination. Most local London deliveries are completed within 2 to 4 hours." },
                  { title: "No Size Limits", desc: "Unlike postal services and app-based couriers, we are not restricted by weight or dimensions. If it fits in a van, we will deliver it. Oversized items are our speciality." },
                  { title: "Real-Time Updates", desc: "Know exactly when your delivery is happening. Our booking confirmation and driver communication keeps you informed throughout the process." },
                  { title: "Transparent Instant Pricing", desc: "Instant, transparent quotes calculated from your postcodes, vehicle and item type. No hidden fees. The price you see online is the price you pay." },
                  { title: "Insured and Professional", desc: "Full goods-in-transit insurance on every delivery. Our drivers are vetted, experienced, and take pride in handling your items with care." },
                  { title: "Open Every Day", desc: "We operate 24 hours a day, 7 days a week, 365 days a year. Bank holidays, weekends, early mornings, and late nights are all covered." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm">{item.title}</h3>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img src={DELIVERY_IMG} alt="Courier delivery service in London" className="w-full h-96 object-cover rounded-xl shadow-lg" />
              <div className="absolute -bottom-6 -left-6 bg-orange-500 text-white rounded-xl p-5 shadow-lg hidden md:block" data-testid="courier-trust-badge">
                <p className="text-3xl font-bold" style={{ fontFamily: 'var(--font-mono)' }}>24/7</p>
                <p className="text-sm font-medium">Same-Day</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-16 md:py-24 bg-white" data-testid="areas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                Courier Coverage Across London
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Our couriers know London like the back of their hand. We collect and deliver in every borough, every postcode, and every neighbourhood. From the financial district to the suburbs, no area is out of reach.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {AREAS.map((area, i) => (
                  <span key={i} className="bg-slate-50 border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full">{area}</span>
                ))}
              </div>
              <p className="text-slate-500 text-sm">
                Plus nationwide delivery to every UK postcode. <a href="tel:+447757133163" className="text-orange-500 hover:underline font-medium">Call us</a> for a quote.
              </p>
            </div>
            <div><img src={LONDON_IMG} alt="London city street" className="w-full h-80 object-cover rounded-xl shadow-lg" /></div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-14 bg-slate-900" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            Send a Parcel Across London Today
          </h2>
          <p className="text-slate-300 mb-8 text-base max-w-2xl mx-auto">
            Get an instant courier quote. Enter your collection and delivery postcodes, choose your parcel size, and see the price immediately. No waiting, no callbacks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate("/")} className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-10 py-6 text-base rounded-md shadow-lg hover:shadow-xl active:scale-95 transition-all" data-testid="cta-get-quote-btn">
              Get Your Free Quote <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <a href="tel:+447757133163" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium px-10 py-3 rounded-md transition-colors border border-white/20" data-testid="cta-call-btn">
              <Phone className="h-5 w-5" /> +44 7757 133163
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Courier Service FAQs</h2>
            <p className="text-slate-600 text-base">Answers to the most common questions about our London courier service.</p>
          </div>
          <div className="space-y-3">
            {FAQ_DATA.map((item, i) => (<FAQItem key={i} question={item.q} answer={item.a} />))}
          </div>
        </div>
      </section>

      {/* SEO Content Block */}
      <section className="py-12 bg-slate-50 border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>London's Reliable Courier Service</h2>
          <div className="prose prose-slate prose-sm max-w-none text-slate-600 leading-relaxed space-y-4">
            <p>In a city that moves as fast as London, having a courier service you can depend on makes all the difference. Whether you are a solicitor sending time-sensitive contracts, a small business dispatching orders, or an individual who needs something collected from a seller across town, Transport Anything 247 gets it there quickly and safely.</p>
            <p>What sets us apart from the large national courier networks is personal service and flexibility. We do not sort your parcel through three depots before it reaches its destination. Our driver collects it from you and takes it directly where it needs to go. That means faster delivery times, less handling, and far less chance of damage or loss.</p>
            <p>Our pricing is dedicated and transparent. Every quote is generated instantly based on distance, vehicle and transport requirements — no weight-based tiers, no zone charges, no fuel surcharges tacked on at checkout. The quote you receive online is the exact amount you pay.</p>
            <p>We also handle items that other couriers will not touch. Oversized boxes, awkwardly shaped items, fragile goods, and heavy packages are all within our capability. And because we operate our own fleet rather than subcontracting, quality and accountability stay high.</p>
            <p>For larger items, explore our <a href="/services/man-and-van-london" className="text-orange-500 hover:underline">man and van service</a>, <a href="/services/car-transport-london" className="text-orange-500 hover:underline">car transport</a>, or <a href="/services/furniture-delivery-london" className="text-orange-500 hover:underline">furniture delivery</a> options across London.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Truck className="h-8 w-8 text-orange-500" />
                <span className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>Transport Anything 247</span>
              </div>
              <p className="text-slate-400 text-sm">Fast, reliable courier service in London and across the UK. Same day delivery, fully insured, available 24/7.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Our Services</h4>
              <div className="space-y-2">
                <a href="/#calculator" className="footer-link block">Get Quote</a>
                <a href="/services/man-and-van-london" className="footer-link block">Man and Van London</a>
                <a href="/services/car-transport-london" className="footer-link block">Car Transport London</a>
                <a href="/services/courier-service-london" className="footer-link block text-orange-400">Courier Service London</a>
                <a href="/services/furniture-delivery-london" className="footer-link block">Furniture Delivery London</a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-slate-400 text-sm">
                <p>Phone: <a href="tel:+447757133163" className="text-orange-400 hover:text-orange-300">+44 7757 133163</a></p>
                <p>Email: <a href="mailto:customers@trasportanything247.com" className="text-orange-400 hover:text-orange-300">customers@trasportanything247.com</a></p>
                <p>Hours: 24/7 - Always Available</p>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-slate-500 text-sm">
            <p>&copy; {new Date().getFullYear()} Transport Anything 247. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
